import React from 'react';
import type { LeaderboardEntry } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { Flame, Star } from 'lucide-react';

interface Props {
  entries: LeaderboardEntry[];
  language: Language;
  activeTab: string;
}

const RANK_BADGE_COLORS: Record<string, string> = {
  'Expert Comptable': 'bg-purple-100 text-purple-700',
  'Comptable Senior': 'bg-blue-100 text-blue-700',
  'Comptable': 'bg-green-100 text-green-700',
  'Assistant Comptable': 'bg-amber-100 text-amber-700',
  'Apprenti': 'bg-orange-100 text-orange-700',
  'Étudiant': 'bg-gray-100 text-gray-600',
};

export default function LeaderboardTable({ entries, language, activeTab }: Props) {
  return (
    <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-muted/40 border-b border-border">
        <div className="col-span-1 text-xs font-600 text-muted-foreground uppercase tracking-wide">
          {t(language, 'lb_rank')}
        </div>
        <div className="col-span-5 text-xs font-600 text-muted-foreground uppercase tracking-wide">
          {t(language, 'lb_player')}
        </div>
        <div className="col-span-2 text-xs font-600 text-muted-foreground uppercase tracking-wide text-right">
          {t(language, 'lb_xp')}
        </div>
        <div className="col-span-2 text-xs font-600 text-muted-foreground uppercase tracking-wide text-right">
          {t(language, 'lb_streak')}
        </div>
        <div className="col-span-2 text-xs font-600 text-muted-foreground uppercase tracking-wide text-right hidden sm:block">
          {language === 'fr' ? 'Rang' : 'Ранг'}
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {entries.map((entry) => (
          <div
            key={`lb-row-${entry.id}`}
            className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center transition-colors ${
              entry.isCurrentUser
                ? 'bg-primary/5 border-l-2 border-l-primary' :'hover:bg-muted/30'
            }`}
          >
            {/* Rank */}
            <div className="col-span-1">
              <span
                className={`text-sm font-800 font-tabular ${
                  entry.rank === 1
                    ? 'text-amber-500'
                    : entry.rank === 2
                    ? 'text-gray-400'
                    : entry.rank === 3
                    ? 'text-orange-600' :'text-muted-foreground'
                }`}
              >
                {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
              </span>
            </div>

            {/* Player */}
            <div className="col-span-5 flex items-center gap-3 min-w-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-700 flex-shrink-0 ${
                  entry.isCurrentUser ? 'ring-2 ring-primary ring-offset-1' : ''
                }`}
                style={{
                  background: `hsl(${(entry.rank * 47) % 360}, 60%, 55%)`,
                }}
              >
                {entry.avatar}
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-600 truncate ${entry.isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                  {entry.name}
                  {entry.isCurrentUser && (
                    <span className="ml-1.5 text-xs font-500 text-primary">
                      {language === 'fr' ? '(vous)' : '(вы)'}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{entry.country === 'FR' ? '🇫🇷' : '🇷🇺'}</p>
              </div>
            </div>

            {/* XP */}
            <div className="col-span-2 text-right">
              <div className="flex items-center justify-end gap-1">
                <Star size={11} className="text-primary" />
                <span className="text-sm font-700 text-foreground font-tabular">
                  {(activeTab === 'weekly' ? entry.weeklyXp : entry.xp).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Streak */}
            <div className="col-span-2 text-right">
              <div className="flex items-center justify-end gap-1">
                <Flame size={11} className="text-accent" />
                <span className="text-sm font-700 text-foreground font-tabular">{entry.streak}</span>
              </div>
            </div>

            {/* Rank title */}
            <div className="col-span-2 text-right hidden sm:block">
              <span
                className={`text-xs font-600 px-2 py-0.5 rounded-full ${
                  RANK_BADGE_COLORS[entry.rankTitle] ?? 'bg-muted text-muted-foreground'
                }`}
              >
                {entry.rankTitle}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}